import React, { useState } from "react";
import { Box, Flex, Heading, HStack, Link, ListItem, OrderedList, Table, Tbody, Td, Text, Th, Thead, Tr, UnorderedList } from "@chakra-ui/react";
import { CaretDown, CaretUp, Sparkle } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CodeExecutionBlock } from "./CodeExecutionBlock";
import { ChatWidget } from "./ChatWidget";
import { ChartRefCard } from "./charts/ChartRefCard";
import { CHART_FENCE, WIDGET_FENCE, isComponentFence } from "./markdownFences";

const markdownComponents = (onSend) => ({
  h1: ({ children }) => (
    <Heading as="h1" size="md" my={2}>
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h2" size="sm" my={2}>
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as="h3" size="xs" my={2}>
      {children}
    </Heading>
  ),
  p: ({ children }) => (
    <Text fontSize="sm" my={1}>
      {children}
    </Text>
  ),
  a: ({ children, href }) => (
    <Link href={href} color="teal.600" isExternal>
      {children}
    </Link>
  ),
  ul: ({ children }) => <UnorderedList pl={4}>{children}</UnorderedList>,
  ol: ({ children }) => <OrderedList pl={4}>{children}</OrderedList>,
  li: ({ children }) => <ListItem fontSize="sm">{children}</ListItem>,
  table: ({ children }) => (
    <Box overflowX="auto" my={2}>
      <Table size="sm" variant="simple">
        {children}
      </Table>
    </Box>
  ),
  thead: ({ children }) => <Thead>{children}</Thead>,
  tbody: ({ children }) => <Tbody>{children}</Tbody>,
  tr: ({ children }) => <Tr>{children}</Tr>,
  th: ({ children }) => <Th fontSize="xs">{children}</Th>,
  td: ({ children }) => <Td fontSize="xs">{children}</Td>,
  // A fence that resolves to a component is NOT wrapped. The `code` renderer below swaps widgets and chart
  // cards in, but ReactMarkdown still nests them as <pre><code>, so without this they rendered inside a
  // preformatted box: the question could not wrap (white-space: pre), the buttons came out monospace, a grey
  // background sat behind the card, and overflowX let the whole widget scroll sideways out of the chat
  // column. The two renderers share one rule so they cannot drift apart — see markdownFences.
  pre: ({ children }) =>
    isComponentFence(children) ? (
      <>{children}</>
    ) : (
      <Box as="pre" bg="gray.100" p={2} borderRadius="md" fontSize="xs" overflowX="auto" my={2}>
        {children}
      </Box>
    ),
  code: ({ inline, className, children, ...props }) => {
    const match = WIDGET_FENCE.exec(className || "");
    if (match) {
      try {
        const config = JSON.parse(String(children).trim());
        return <ChatWidget type={match[1]} config={config} onSend={onSend} />;
      } catch {
        // fall through to default code rendering
      }
    }
    if (CHART_FENCE.test(className || "")) {
      try {
        const { chart_id: chartId } = JSON.parse(String(children).trim());
        if (chartId) return <ChartRefCard chartId={chartId} />;
      } catch {
        // fall through to default code rendering
      }
    }
    if (inline) {
      return (
        <Box
          as="code"
          bg="gray.100"
          px={1}
          borderRadius="sm"
          fontSize="xs"
          fontFamily="mono"
          {...props}
        >
          {children}
        </Box>
      );
    }
    return (
      <Box as="code" fontFamily="mono" fontSize="xs" {...props}>
        {children}
      </Box>
    );
  },
});

// Long pasted prompts (e.g. the interactive session setup block) swamp the
// chat history — collapse them to a one-line summary with a toggle.
const COLLAPSE_THRESHOLD = 600;

const summariseContent = (content) => {
  const firstLine = content.trim().replace(/^["'\s]+/, "").split(/[\n.]/)[0].trim();
  return firstLine.length > 120 ? `${firstLine.slice(0, 120)}…` : firstLine;
};

const CollapsibleUserMessage = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Flex justify="flex-end" pl={8}>
      <Box bg="pureBlack" borderRadius="2xl" px={4} py={3} maxW="85%">
        {isExpanded ? (
          <Text whiteSpace="pre-wrap" wordBreak="break-word" fontSize="14px" color="white" lineHeight="1.45">
            {content}
          </Text>
        ) : (
          <Text fontSize="14px" color="white" lineHeight="1.45" noOfLines={2}>
            {summariseContent(content)}
          </Text>
        )}
        <HStack
          as="button"
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          spacing={1}
          mt={2}
          color="gray.400"
          _hover={{ color: "white" }}
          transition="color 0.15s"
        >
          {isExpanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
          <Text fontSize="12px" fontWeight="600">
            {isExpanded ? "Collapse prompt" : "Show full prompt"}
          </Text>
        </HStack>
      </Box>
    </Flex>
  );
};

export function ChatMessage({ message, onSend }) {
  if (message.codeExecution) {
    return (
      <Box mr={8}>
        <CodeExecutionBlock
          code={message.codeExecution.code}
          output={message.codeExecution.output}
        />
      </Box>
    );
  }

  // A turn the app started for the user (analysing a file straight after upload). Live, it arrives as
  // role "system"; after a reload it comes back as role "user" carrying `auto`, because the message
  // table's role CHECK allows only user/assistant and the display text has to live on the user row.
  // Both must render the same way, or the transcript changes appearance on refresh.
  if (message.role === "system" || message.auto) {
    // A failed turn (isError) must read as distinctly different from routine
    // notices like "Using tool: ...", not identical faint grey text — otherwise
    // a crashed turn looks like normal chatter and users miss it entirely.
    if (message.isError) {
      return (
        <Box bg="red.50" border="1px solid" borderColor="esg_red" borderRadius="md" px={3} py={2} my={1}>
          <Text fontSize="xs" color="esg_red" fontWeight="600" whiteSpace="pre-wrap" wordBreak="break-word">
            {message.content}
          </Text>
        </Box>
      );
    }
    return (
      <Text fontSize="xs" color="gray.500" textAlign="center" py={1}>
        {message.content}
      </Text>
    );
  }

  if (message.role === "user") {
    if ((message.content?.length ?? 0) > COLLAPSE_THRESHOLD) {
      return <CollapsibleUserMessage content={message.content} />;
    }
    return (
      <Flex justify="flex-end" pl={8}>
        <Box bg="pureBlack" borderRadius="2xl" px={4} py={3} maxW="85%">
          <Text whiteSpace="pre-wrap" wordBreak="break-word" fontSize="14px" color="white" lineHeight="1.45">
            {message.content}
          </Text>
        </Box>
      </Flex>
    );
  }

  // assistant — sparkle avatar to the left of a white card
  return (
    <Flex align="flex-start" gap={2} pr={8}>
      <Box
        flexShrink={0}
        mt={1}
        w="24px"
        h="24px"
        borderRadius="md"
        bg="lightLime"
        border="1px solid"
        borderColor="lime"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Sparkle size={12} weight="fill" color="#85C22A" />
      </Box>
      <Box flex="1" minW={0} bg="white" border="1px solid" borderColor="gray.200" borderRadius="2xl" px={4} py={3}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents(onSend)}>
          {message.content}
        </ReactMarkdown>
        {message.isStreaming && (
          <Box
            as="span"
            display="inline-block"
            w="2px"
            h="14px"
            bg="limeShade.600"
            ml={1}
            opacity={0.6}
          />
        )}
      </Box>
    </Flex>
  );
}
